import { selectRoomStaysEnabled } from 'reduxStore/roomStays/selectors';
import { roomStaysActions } from 'reduxStore/roomStays/slice';
import { apiFailureMessage } from 'library/helpers/apiFailureMessage';
import { CREDENTIAL_TYPE_MOBILE } from 'config/credentialTypes';
import {
	credentialCreatedNotice,
	credentialWriteRefusalMessage,
	mobileIssuedNotice
} from 'library/helpers/credentials';
import SuperFetch from 'library/helpers/superFetch';
import { formatDisplayName } from 'library/helpers/utility';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { selectPeopleByPersonID } from 'reduxStore/people/selectors';
import { Notification } from 'zComponents/atoms';
import { watchActivity } from '../_shared/activity';
import { credentialsActions } from './slice';

// A refused /credentials write: a known errorCode's one-liner, else the server's own reason.
const credentialErrorText = (res, fallback, data) =>
	credentialWriteRefusalMessage(res?.errorCode, data) || apiFailureMessage(res, fallback);

// GET ALL CREDENTIALS (with loading spinner — initial load + site switch)
export function* getCredentials() {
	try {
		const { orgID, siteID } = yield select((state) => state.Auth.selectedProfile);
		yield put(credentialsActions.loadCredentialsStart({ orgID, siteID }));
		const res = yield call(() => SuperFetch.get('/credentials'));

		if (res.statusCode === 200) {
			yield put(credentialsActions.loadCredentialsSuccess({ orgID, siteID, data: res.data }));
		} else {
			throw Error(res.error?.message || res.error || res.info || 'Failed to load credentials.');
		}
	} catch (error) {
		const { orgID, siteID } = yield select((state) => state.Auth.selectedProfile);
		yield put(credentialsActions.loadCredentialsError({ orgID, siteID, error }));
		Notification('error', error.message);
	}
}

const LAST_USED_TTL_MS = 2 * 60 * 1000;

// Last-used times for the Credentials table only: the flag costs up to ~1 s on large orgs, so the shared
// list never sends it. Stores just credentialID → lastUsedAt (latest access-log event, success or failure).
export function* getCredentialsLastUsed({ payload } = {}) {
	const { orgID, siteID } = yield select((state) => state.Auth.selectedProfile);
	const cached = yield select((state) => state.Credentials.lastUsed?.[orgID]?.[siteID || 'all']);
	if (!payload?.force && (cached?.loading || (cached?.ts && Date.now() - cached.ts < LAST_USED_TTL_MS))) return;
	yield put(credentialsActions.loadCredentialsLastUsedStart({ orgID, siteID }));
	try {
		const res = yield call(() => SuperFetch.get('/credentials', {}, { includeLastUsed: true }));
		if (res.statusCode !== 200) throw new Error(res.error?.message || res.error || 'Failed to load last used.');
		const byID = Object.fromEntries((res.data || []).map((c) => [c.credentialID, c.lastUsedAt ?? null]));
		yield put(credentialsActions.loadCredentialsLastUsedSuccess({ orgID, siteID, byID, ts: Date.now() }));
	} catch {
		// A missing column is not worth a toast: the table shows "—".
		yield put(credentialsActions.loadCredentialsLastUsedError({ orgID, siteID }));
	}
}

// REFRESH CREDENTIALS (silent — no loading spinner, used after CRUD operations)
export function* refreshCredentials() {
	try {
		const { orgID, siteID } = yield select((state) => state.Auth.selectedProfile);
		const res = yield call(() => SuperFetch.get('/credentials'));

		if (res.statusCode === 200) {
			yield put(credentialsActions.refreshCredentialsSuccess({ orgID, siteID, data: res.data }));
		}
	} catch (error) {
		// Silent refresh — don't show error notifications
	}
}

// CREATE CREDENTIAL
export function* createCredential({ payload }) {
	try {
		const { data } = payload;
		const res = yield call(() => SuperFetch.post('/credentials', data));

		if (res.statusCode === 200) {
			yield put(credentialsActions.createCredentialSuccess());
			if (data?.credentialTypeID === CREDENTIAL_TYPE_MOBILE) {
				const notice = mobileIssuedNotice(res.data);
				Notification(notice.type, notice.title, notice.text);
				return;
			}
			// An existing card is never "created": say whether it was kept, assigned or moved.
			const previousHolderID = res.data?.existing ? res.data.previousHolderID : null;
			const previousHolder = previousHolderID
				? yield select((state) => selectPeopleByPersonID(state, previousHolderID))
				: null;
			const notice = credentialCreatedNotice(
				res.data,
				data?.personID ?? null,
				previousHolder ? formatDisplayName(previousHolder).displayName : null
			);
			Notification(notice.type, notice.title, notice.text);
		} else {
			throw Error(credentialErrorText(res, 'Failed to create credential.'));
		}
	} catch (error) {
		yield put(credentialsActions.createCredentialError(error));
		Notification('error', error.message);
	}
}

// A held card (new, assigned or moved) is already on its way to the locks.
export function* createCredentialSuccess() {
	yield put(credentialsActions.refreshCredentials());
	yield put(credentialsActions.fetchPropagation());
}

// UPDATE CREDENTIAL
export function* updateCredential({ payload }) {
	const { credentialID, data } = payload;
	try {
		const res = yield call(() => SuperFetch.patch(`/credentials/${credentialID}`, data));

		if (res.statusCode === 200) {
			yield put(credentialsActions.updateCredentialSuccess({ credentialID }));
			Notification('success', 'Credential updated successfully.');
		} else {
			throw Error(credentialErrorText(res, 'Failed to update credential.', data));
		}
	} catch (error) {
		yield put(credentialsActions.updateCredentialError({ credentialID, error: error.message }));
		Notification('error', error.message);
	}
}

export function* updateCredentialSuccess() {
	yield put(credentialsActions.refreshCredentials());
}

// DELETE CREDENTIAL
export function* deleteCredential({ payload }) {
	const { credentialID } = payload;
	try {
		const res = yield call(() => SuperFetch.delete(`/credentials/${credentialID}`));

		if (res.statusCode === 200) {
			yield put(credentialsActions.deleteCredentialSuccess({ credentialID }));
			// A fingerprint delete clears the ledger only: the template still opens the door until removed
			// at the lock. `info` says so and `data.remaining` names the lock.
			if (res.data?.lockSideRemoved === false) {
				const where = (res.data.remaining || [])
					.map((r) => `lock ${r.deviceID}${r.fingerprintNumber ? ` (#${r.fingerprintNumber})` : ''}`)
					.join(', ');
				Notification(
					'warning',
					'Removed from the inventory only',
					res.info ||
						`The template REMAINS on ${where || 'the lock'} and still opens the door — remove it at the lock.`
				);
			} else {
				Notification('success', 'Credential deleted successfully.');
			}
		} else {
			throw Error(credentialErrorText(res, 'Failed to delete credential.'));
		}
	} catch (error) {
		yield put(credentialsActions.deleteCredentialError({ credentialID, error: error.message }));
		Notification('error', error.message);
	}
}

export function* deleteCredentialSuccess() {
	yield put(credentialsActions.refreshCredentials());
}

// ✅ DELETE CREDENTIALS (bulk) — single or multiple deletions via the bulk endpoint.
// The backend deletes each credential independently — one failure doesn't abort the
// rest, so the response reports per-credential results plus a { deleted, failed } summary.
export function* deleteCredentials({ payload }) {
	const { credentialIDs } = payload;
	try {
		// Bulk endpoint accepts at most 50 IDs per request
		const chunks = [];
		for (let i = 0; i < credentialIDs.length; i += 50) chunks.push(credentialIDs.slice(i, i + 50));

		const responses = yield all(
			chunks.map((chunk) => call(() => SuperFetch.delete('/credentials/bulk', { credentialIDs: chunk })))
		);

		let deleted = 0;
		let failed = 0;
		// Credentials whose LEDGER row went but whose credential is still on the lock — fingerprints.
		// The API counts these separately precisely so they are not reported as deletions; summing
		// only `deleted` made a bulk delete of 10 fingerprints render "Credential has been
		// permanently deleted" (singular, count 0) while 10 templates stayed on their locks.
		let ledgerOnly = 0;
		responses.forEach((res) => {
			if (![200, 204].includes(res.statusCode)) {
				throw Error(res.error?.message || res.error || res.info || 'Failed to delete credential(s).');
			}
			const summary = res.data?.summary || res.summary || {};
			deleted += summary.deleted || 0;
			failed += summary.failed || 0;
			ledgerOnly += summary.ledgerOnly || 0;
		});

		if (deleted === 0 && ledgerOnly === 0 && failed > 0) {
			throw Error(`Failed to delete ${failed} credential${failed > 1 ? 's' : ''}.`);
		}

		yield put(credentialsActions.deleteCredentialsSuccess({ credentialIDs }));

		const plural = (n, noun) => `${n} ${noun}${n === 1 ? '' : 's'}`;
		if (failed > 0) {
			Notification(
				'warning',
				'Some credentials were not deleted',
				[
					deleted > 0 && `${plural(deleted, 'deleted')}`,
					ledgerOnly > 0 && `${ledgerOnly} removed from the inventory only`,
					`${failed} failed`
				]
					.filter(Boolean)
					.join(', ') + '.'
			);
		} else if (ledgerOnly > 0) {
			// Never "permanently deleted" when a template is still on a lock.
			Notification(
				'warning',
				deleted > 0 ? 'Partly removed from the inventory only' : 'Removed from the inventory only',
				`${plural(ledgerOnly, 'credential')} still on the lock and must be removed there` +
					(deleted > 0 ? `; ${deleted} permanently deleted.` : '.')
			);
		} else {
			Notification(
				'success',
				deleted > 1 ? 'Credentials deleted' : 'Credential deleted',
				deleted > 1
					? `${deleted} credentials have been permanently deleted.`
					: 'Credential has been permanently deleted.'
			);
		}
	} catch (error) {
		yield put(credentialsActions.deleteCredentialsError({ credentialIDs, error: error.message }));
		Notification('error', 'Delete failed', error.message || 'Failed to delete credential(s).');
	}
}

/**
 * Floating room cards: the server answers an assign to a designated room with one of these instead of assigning, and
 * carries the stay preview. The confirmation modal shows it and re-sends this same assign with `roomStay`.
 */
export const ROOM_STAY_CONFIRMATION_REASONS = [
	'ROOM_STAY_CONFIRMATION_REQUIRED',
	'ROOM_STAY_STALE',
	'NO_APPLICABLE_STAY'
];

/**
 * The toast for an accepted assign. Plain assigns (no room) succeed as before; a room operation is named by its status,
 * not its hardware state alone, so a superseded issue is never called accepted (DA1-06, DB1-12), and cards retired from
 * the previous stay are named (DA1-03, the part the response already carries).
 */
export const roomOutcomeNotice = (data) => {
	const status = data?.status;
	const hardware = data?.hardwareState;
	const why = (data?.reasons || [])
		.map((r) => r.message)
		.filter(Boolean)
		.join(' ');
	const retired = (data?.retiredCredentialIDs || []).length;
	const retiredNote = retired
		? ` ${retired} card${retired === 1 ? '' : 's'} from the previous stay ${retired === 1 ? 'was' : 'were'} moved back to Base cards.`
		: '';
	if (status === 'superseded') {
		return {
			tone: 'warning',
			title: 'Not done — the stay or card changed',
			detail: why || 'Nothing more was done. Check the room and try again.'
		};
	}
	if (hardware === 'blocked' || status === 'cleanup_blocked') {
		return {
			tone: 'warning',
			title: 'Accepted — needs attention',
			detail: `${why || 'A lock or the panel has not confirmed yet.'}${retiredNote}`.trim()
		};
	}
	if (hardware === 'pending' || status === 'delivery_pending' || status === 'accepted') {
		return {
			tone: 'warning',
			title: 'Accepted — sending to locks',
			detail: `${why || 'The room is not ready yet; it completes when every lock and panel confirms.'}${retiredNote}`.trim()
		};
	}
	return { tone: 'success', title: 'Credential assigned successfully.', detail: retiredNote.trim() || undefined };
};

// ASSIGN CREDENTIAL
export function* assignCredential({ payload }) {
	const { credentialID, data } = payload;
	try {
		const res = yield call(() => SuperFetch.patch(`/credentials/${credentialID}/assign`, data));
		const reason = res?.data?.reason;

		if (res.statusCode === 200) {
			yield put(credentialsActions.assignCredentialSuccess({ credentialID }));
			// A room issue or return is accepted before the locks confirm; never call it done while hardware is pending.
			const { title, tone, detail } = roomOutcomeNotice(res.data);
			if (detail) Notification(tone, title, detail);
			else Notification(tone, title);
			yield put(roomStaysActions.dismissIssueConfirmation({ credentialID }));
			// A room operation changed some room's cards: reload every room list on screen so it shows them (DB2-02).
			if (res?.data?.operationID) {
				const loaded = yield select((state) => Object.keys(state.RoomStays?.staysByHolder || {}));
				for (const holderID of loaded) yield put(roomStaysActions.loadStays({ holderID: Number(holderID) }));
			}
		} else if (ROOM_STAY_CONFIRMATION_REASONS.includes(reason)) {
			// The confirmation is hidden while the client flag is off (not loaded yet, or switched on mid-session): say
			// what the server said rather than doing nothing (DB1-02, DA1-10).
			if (!(yield select(selectRoomStaysEnabled))) {
				throw Error(res.info || 'This card is for a room: reload the page to confirm the stay.');
			}
			// Not an error to toast: the confirmation shows the stay (or why there is none) and re-sends.
			yield put(credentialsActions.assignCredentialError({ credentialID, error: null }));
			yield put(
				roomStaysActions.requestIssueConfirmation({
					credentialID,
					data: { newPersonID: data?.newPersonID },
					reason,
					preview: res.data.preview || null,
					message: res.info || null
				})
			);
		} else {
			throw Error(credentialErrorText(res, 'Failed to assign credential.'));
		}
	} catch (error) {
		yield put(credentialsActions.assignCredentialError({ credentialID, error: error.message }));
		Notification('error', error.message);
	}
}

export function* assignCredentialSuccess() {
	yield put(credentialsActions.refreshCredentials());
	yield put(credentialsActions.fetchPropagation());
}

// UNASSIGN CREDENTIAL
export function* unassignCredential({ payload }) {
	const { credentialID, data } = payload;
	try {
		const res = yield call(() => SuperFetch.patch(`/credentials/${credentialID}/unassign`, data));

		if (res.statusCode === 200) {
			yield put(credentialsActions.unassignCredentialSuccess({ credentialID }));
			Notification('success', 'Credential unassigned successfully.');
		} else {
			throw Error(res.error?.message || res.error || res.info || 'Failed to unassign credential.');
		}
	} catch (error) {
		yield put(credentialsActions.unassignCredentialError({ credentialID, error: error.message }));
		Notification('error', error.message);
	}
}

export function* unassignCredentialSuccess() {
	yield put(credentialsActions.refreshCredentials());
	yield put(credentialsActions.fetchPropagation());
}

// ASSOCIATE CARD (card-specific — links CSN cross-integration)
export function* associateCard({ payload }) {
	const { credentialID, data } = payload;
	try {
		const res = yield call(() => SuperFetch.patch(`/credentials/${credentialID}/associate`, data));

		if (res.statusCode === 200) {
			yield put(credentialsActions.associateCardSuccess({ credentialID }));
			Notification('success', 'Card associated successfully.');
		} else {
			throw Error(res.error?.message || res.error || res.info || 'Failed to associate card.');
		}
	} catch (error) {
		yield put(credentialsActions.associateCardError({ credentialID, error: error.message }));
		Notification('error', error.message);
	}
}

export function* associateCardSuccess() {
	yield put(credentialsActions.refreshCredentials());
}

// UPDATE CREDENTIAL SITES
export function* updateCredentialSites({ payload }) {
	try {
		const { credentialID, siteIDs } = payload;
		const res = yield call(() => SuperFetch.put(`/credentials/${credentialID}/sites`, { siteIDs }));

		if (res.statusCode === 200) {
			yield put(credentialsActions.updateCredentialSitesSuccess());
			Notification('success', 'Credential sites updated successfully.');
		} else {
			throw Error(res.error?.message || res.error || res.info || 'Failed to update credential sites.');
		}
	} catch (error) {
		yield put(credentialsActions.updateCredentialSitesError(error));
		Notification('error', error.message);
	}
}

export function* updateCredentialSitesSuccess() {
	yield put(credentialsActions.refreshCredentials());
}

// BATCH IR CARDS
export function* createCardsBatchIR({ payload }) {
	try {
		const { data } = payload;
		const res = yield call(() => SuperFetch.post('/credentials/batch-ir', data));

		if (res.statusCode === 200) {
			yield put(credentialsActions.createCardsBatchIRSuccess());
		} else {
			throw Error(res.error?.message || res.error || res.info || 'Failed to create IR batch cards.');
		}
	} catch (error) {
		yield put(credentialsActions.createCardsBatchIRError(error));
		Notification('error', error.message);
	}
}

export function* createCardsBatchIRSuccess() {
	yield put(credentialsActions.refreshCredentials());
}

// BATCH CONTROL CARDS
export function* createCardsBatch({ payload }) {
	try {
		const { data } = payload;
		const res = yield call(() => SuperFetch.post('/credentials/batch', data));

		if (res.statusCode === 200) {
			yield put(credentialsActions.createCardsBatchSuccess());
		} else {
			throw Error(res.error?.message || res.error || res.info || 'Failed to create batch cards.');
		}
	} catch (error) {
		yield put(credentialsActions.createCardsBatchError(error));
		Notification('error', error.message);
	}
}

export function* createCardsBatchSuccess() {
	yield put(credentialsActions.refreshCredentials());
}

// BATCH CARD INVENTORY (Zezamii card) — raw {wiegandSiteID, wiegandCardNumber, csn} payload,
// no remapping to the legacy {name, value, uniqueIdentifier} shape (backend's
// createCardBatchInventorySchema takes the inventory fields as-is).
export function* createCardsBatchInventory({ payload }) {
	try {
		const { data } = payload;
		const res = yield call(() => SuperFetch.post('/credentials/batch-inventory', data));

		if (res.statusCode === 200) {
			yield put(credentialsActions.createCardsBatchInventorySuccess());
		} else {
			throw Error(res.error?.message || res.error || res.info || 'Failed to create card inventory batch.');
		}
	} catch (error) {
		yield put(credentialsActions.createCardsBatchInventoryError(error));
		Notification('error', error.message);
	}
}

export function* createCardsBatchInventorySuccess() {
	yield put(credentialsActions.refreshCredentials());
}

// FETCH PROPAGATION — one snapshot feeds the Active tab (credentialPropagation) and the failed rows
// (propagationStatus). Every socket signal lands here; the state is replaced, never merged.
export function* fetchPropagation() {
	try {
		const { orgID, siteID } = yield select((state) => state.Auth.selectedProfile);
		const query = siteID && siteID !== 'all' ? { siteID } : {};
		const res = yield call(() => SuperFetch.get('/credentials/propagation', {}, query));
		if (res.statusCode === 200 && res.data) {
			const { queued, active, completed, failed } = res.data;
			// scope: a switch drops pending Retry placeholders so orgs/sites never mix
			yield put(
				credentialsActions.hydrateCredentialPropagation({
					queued,
					active,
					completed,
					scope: `${orgID}:${siteID}`
				})
			);
			yield put(credentialsActions.fetchPropagationStatusSuccess(failed || []));
		}
	} catch (error) {
		// Silent — rehydration is best-effort
	}
}

// RETRY CREDENTIAL PROPAGATION (fire-and-forget — the socket signal refetches the snapshot)
export function* retryCredentialPropagation({ payload }) {
	try {
		const { credentialID, deviceIDs } = payload;
		const res = yield call(() => SuperFetch.post(`/credentials/${credentialID}/retry`, { deviceIDs }));

		if (res.statusCode !== 200) {
			throw Error(res.error?.message || res.error || res.info || 'Failed to retry propagation.');
		}
		yield put(credentialsActions.credentialPropagationRetryAccepted({ credentialID }));
	} catch (error) {
		// No job started: drop the placeholder so it can't spin, then re-hydrate the Failed/Dead tabs.
		yield put(credentialsActions.credentialPropagationRetryFailed(payload));
		yield put(credentialsActions.fetchPropagation());
		Notification('error', error.message);
	}
}

// ─── CLOUD KEYS (visitor credentials, Cloud Key WS1) ───────────────────────────
// Loaded from the dedicated /credentials/cloud-keys endpoint (org + site scoped).
// The list pages at 50 by default; ask for the server max (500) so tabs and counts are not cut short.
const cloudKeyQuery = (siteID) => ({ ...(siteID && siteID !== 'all' ? { siteID } : {}), pageSize: 500 });

// LIST (with loading spinner — initial load + site switch)
export function* getCloudKeys() {
	try {
		const { orgID, siteID } = yield select((state) => state.Auth.selectedProfile);
		yield put(credentialsActions.loadCloudKeysStart({ orgID, siteID }));
		const res = yield call(() => SuperFetch.get('/credentials/cloud-keys', {}, cloudKeyQuery(siteID)));

		if (res.statusCode === 200) {
			yield put(credentialsActions.loadCloudKeysSuccess({ orgID, siteID, data: res.data }));
		} else {
			throw Error(res.error?.message || res.error || res.info || 'Failed to load cloud keys.');
		}
	} catch (error) {
		const { orgID, siteID } = yield select((state) => state.Auth.selectedProfile);
		yield put(credentialsActions.loadCloudKeysError({ orgID, siteID, error }));
		Notification('error', error.message);
	}
}

// REFRESH (silent — after issue/revoke, no loading flash)
export function* refreshCloudKeys() {
	try {
		const { orgID, siteID } = yield select((state) => state.Auth.selectedProfile);
		const res = yield call(() => SuperFetch.get('/credentials/cloud-keys', {}, cloudKeyQuery(siteID)));
		if (res.statusCode === 200) {
			yield put(credentialsActions.refreshCloudKeysSuccess({ orgID, siteID, data: res.data }));
		}
	} catch (error) {
		// Silent refresh — no error notification.
	}
}

// PROVISION (create + return the one-time URL; no email)
export function* provisionCloudKey({ payload }) {
	try {
		const res = yield call(() => SuperFetch.post('/credentials/cloud-keys/provision', payload.data));

		if (res.statusCode === 200 || res.statusCode === 201) {
			yield put(credentialsActions.issueCloudKeySuccess(res.data));
			yield put(credentialsActions.refreshCloudKeys());
			Notification('success', 'Cloud key created.');
		} else {
			throw Error(res.error?.message || res.error || res.info || 'Failed to create cloud key.');
		}
	} catch (error) {
		yield put(credentialsActions.issueCloudKeyError({ error: error.message }));
		Notification('error', error.message);
	}
}

// SEND (provision + email). Email-failure semantics: the key WAS created — the
// backend returns 502 with data.credentialID. Keep the key, surface for resend.
export function* sendCloudKey({ payload }) {
	try {
		const res = yield call(() => SuperFetch.post('/credentials/cloud-keys/send', payload.data));

		if (res.statusCode === 200 || res.statusCode === 201) {
			yield put(credentialsActions.issueCloudKeySuccess(res.data));
			yield put(credentialsActions.refreshCloudKeys());
			Notification('success', 'Cloud key sent.');
		} else if (res.statusCode === 502 && res.data?.credentialID) {
			const msg = res.error?.message || res.error || 'Cloud key created, but the link could not be delivered.';
			yield put(credentialsActions.issueCloudKeyError({ error: msg, credentialID: res.data.credentialID }));
			yield put(credentialsActions.refreshCloudKeys());
			Notification('warning', msg);
		} else {
			throw Error(res.error?.message || res.error || res.info || 'Failed to send cloud key.');
		}
	} catch (error) {
		yield put(credentialsActions.issueCloudKeyError({ error: error.message }));
		Notification('error', error.message);
	}
}

// RESEND / regenerate a cloud key — server re-mints (previous link stops working) and
// re-delivers over the saved/overridden channel; link-only returns the new URL to copy.
export function* resendCloudKey({ payload }) {
	const { credentialID, deliveryChannel } = payload;
	try {
		const body = deliveryChannel ? { deliveryChannel } : {};
		const res = yield call(() => SuperFetch.post(`/credentials/cloud-keys/${credentialID}/resend`, body));

		if (res.statusCode === 200 || res.statusCode === 201) {
			yield put(credentialsActions.resendCloudKeySuccess(res.data)); // { credentialID, url, deliveryStatus }
			yield put(credentialsActions.refreshCloudKeys());
			Notification('success', 'Cloud Key resent — a new link was generated.');
		} else if (res.statusCode === 502 && res.data?.credentialID) {
			// Key re-minted but delivery failed — the previous link is already dead.
			const msg = res.error?.message || res.error || 'New Cloud Key link created, but it could not be delivered.';
			yield put(credentialsActions.resendCloudKeyError({ credentialID, error: msg }));
			yield put(credentialsActions.refreshCloudKeys());
			Notification('warning', msg);
		} else {
			throw Error(res.error?.message || res.error || res.info || 'Failed to resend Cloud Key.');
		}
	} catch (error) {
		yield put(credentialsActions.resendCloudKeyError({ credentialID, error: error.message }));
		Notification('error', error.message);
	}
}

// REVOKE a single cloud key
export function* revokeCloudKey({ payload }) {
	const { credentialID, reason } = payload;
	try {
		const res = yield call(() => SuperFetch.patch(`/credentials/cloud-keys/${credentialID}/revoke`, { reason }));

		if (res.statusCode === 200) {
			yield put(credentialsActions.revokeCloudKeySuccess({ credentialID }));
			yield put(credentialsActions.refreshCloudKeys());
			Notification('success', 'Cloud key revoked.');
		} else {
			throw Error(res.error?.message || res.error || res.info || 'Failed to revoke cloud key.');
		}
	} catch (error) {
		yield put(credentialsActions.revokeCloudKeyError({ credentialID, error: error.message }));
		Notification('error', error.message);
	}
}

// ONE KEY with its doors, for the credential drawer
export function* loadCloudKeyDetail({ payload }) {
	const { credentialID } = payload;
	try {
		const res = yield call(() => SuperFetch.get(`/credentials/cloud-keys/${credentialID}`));

		if (res.statusCode === 200) {
			yield put(credentialsActions.loadCloudKeyDetailSuccess({ credentialID, data: res.data }));
		} else {
			throw Error(res.error?.message || res.error || res.info || 'Failed to load Cloud Key.');
		}
	} catch (error) {
		yield put(credentialsActions.loadCloudKeyDetailError({ credentialID }));
		Notification('error', error.message);
	}
}

// LOCKDOWN — read the org's emergency kill-switch state
export function* loadCloudKeyLockdown() {
	try {
		const res = yield call(() => SuperFetch.get('/credentials/cloud-keys/lockdown'));
		if (res.statusCode === 200) {
			yield put(credentialsActions.loadCloudKeyLockdownSuccess(res.data || {}));
		} else {
			throw Error(res.error?.message || res.error || res.info || 'Failed to load lockdown state.');
		}
	} catch (error) {
		yield put(credentialsActions.loadCloudKeyLockdownError());
	}
}

// LOCKDOWN — engage (isLockedDown true) or lift (false). Refetches authoritative state.
export function* setCloudKeyLockdown({ payload }) {
	const { isLockedDown, reason } = payload;
	try {
		const path = isLockedDown ? '/credentials/cloud-keys/lockdown' : '/credentials/cloud-keys/lift-lockdown';
		const res = yield call(() => SuperFetch.post(path, { reason: reason || null }));

		if (res.statusCode === 200) {
			yield put(
				credentialsActions.setCloudKeyLockdownSuccess({ isLockedDown, reason: isLockedDown ? reason : null })
			);
			yield put(credentialsActions.loadCloudKeyLockdown());
			Notification('success', isLockedDown ? 'Visitor key unlocks locked down.' : 'Lockdown lifted.');
		} else {
			throw Error(res.error?.message || res.error || res.info || 'Failed to update lockdown.');
		}
	} catch (error) {
		yield put(credentialsActions.setCloudKeyLockdownError());
		Notification('error', error.message);
	}
}

// ROOT SAGA
export default function* credentialsSaga() {
	yield* watchActivity(credentialsActions);
	yield takeLatest(credentialsActions.loadCredentials, getCredentials);
	yield takeLatest(credentialsActions.refreshCredentials, refreshCredentials);
	// takeEvery + the in-flight guard: takeLatest would cancel the request the guard is waiting on.
	yield takeEvery(credentialsActions.loadCredentialsLastUsed, getCredentialsLastUsed);

	// CRUD
	yield takeLatest(credentialsActions.createCredential, createCredential);
	yield takeLatest(credentialsActions.createCredentialSuccess, createCredentialSuccess);
	yield takeEvery(credentialsActions.updateCredential, updateCredential);
	yield takeEvery(credentialsActions.updateCredentialSuccess, updateCredentialSuccess);
	yield takeLatest(credentialsActions.deleteCredential, deleteCredential);
	yield takeLatest(credentialsActions.deleteCredentialSuccess, deleteCredentialSuccess);
	yield takeLatest(credentialsActions.deleteCredentials, deleteCredentials);
	yield takeLatest(credentialsActions.deleteCredentialsSuccess, deleteCredentialSuccess);

	// Assign / Unassign
	yield takeEvery(credentialsActions.assignCredential, assignCredential);
	yield takeLatest(credentialsActions.assignCredentialSuccess, assignCredentialSuccess);
	yield takeEvery(credentialsActions.unassignCredential, unassignCredential);
	yield takeLatest(credentialsActions.unassignCredentialSuccess, unassignCredentialSuccess);

	// Card-specific
	yield takeLatest(credentialsActions.associateCard, associateCard);
	yield takeLatest(credentialsActions.associateCardSuccess, associateCardSuccess);
	yield takeLatest(credentialsActions.createCardsBatchIR, createCardsBatchIR);
	yield takeLatest(credentialsActions.createCardsBatchIRSuccess, createCardsBatchIRSuccess);
	yield takeLatest(credentialsActions.createCardsBatch, createCardsBatch);
	yield takeLatest(credentialsActions.createCardsBatchSuccess, createCardsBatchSuccess);
	yield takeLatest(credentialsActions.createCardsBatchInventory, createCardsBatchInventory);
	yield takeLatest(credentialsActions.createCardsBatchInventorySuccess, createCardsBatchInventorySuccess);

	// Credential Sites
	yield takeLatest(credentialsActions.updateCredentialSites, updateCredentialSites);
	yield takeLatest(credentialsActions.updateCredentialSitesSuccess, updateCredentialSitesSuccess);

	// Propagation
	yield takeLatest(credentialsActions.fetchPropagation, fetchPropagation);
	yield takeEvery(credentialsActions.retryCredentialPropagation, retryCredentialPropagation);

	// Cloud Keys (visitor credentials)
	yield takeLatest(credentialsActions.loadCloudKeys, getCloudKeys);
	yield takeLatest(credentialsActions.refreshCloudKeys, refreshCloudKeys);
	yield takeLatest(credentialsActions.provisionCloudKey, provisionCloudKey);
	yield takeLatest(credentialsActions.sendCloudKey, sendCloudKey);
	yield takeEvery(credentialsActions.revokeCloudKey, revokeCloudKey);
	yield takeEvery(credentialsActions.resendCloudKey, resendCloudKey);
	yield takeEvery(credentialsActions.loadCloudKeyDetail, loadCloudKeyDetail);
	yield takeLatest(credentialsActions.loadCloudKeyLockdown, loadCloudKeyLockdown);
	yield takeLatest(credentialsActions.setCloudKeyLockdown, setCloudKeyLockdown);
}
